import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useAcquireValidationLockMutation,
  useReleaseValidationLockMutation,
  useRenewValidationLockMutation,
} from '../../../gql/generated/graphql';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseValidationLockOptions {
  companyId: number | null;
  userId: number | null;
  graphqlEndpoint?: string;
}

export const useValidationLock = ({ companyId, userId, graphqlEndpoint }: UseValidationLockOptions) => {
  const [lockAcquired, setLockAcquired] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const lockParamsRef = useRef({ companyId, userId });

  const [acquireLock] = useAcquireValidationLockMutation();
  const [renewLock] = useRenewValidationLockMutation();
  const [releaseLockMutation] = useReleaseValidationLockMutation();

  // Keep ref in sync
  useEffect(() => {
    lockParamsRef.current = { companyId, userId };
  }, [companyId, userId]);

  // Acquire lock on mount
  useEffect(() => {
    if (!companyId || !userId) return;

    const acquire = async () => {
      try {
        const result = await acquireLock({ variables: { companyId, userId } });
        const data = result.data?.acquireValidationLock;
        if (data?.success) {
          setLockAcquired(true);
          setLockError(null);
        } else {
          setLockAcquired(false);
          setLockError(data?.message || 'Failed to acquire lock');
        }
      } catch (err) {
        setLockAcquired(false);
        setLockError('Network error acquiring lock');
      }
    };

    acquire();

    // Start heartbeat
    heartbeatRef.current = setInterval(async () => {
      const { companyId: cId, userId: uId } = lockParamsRef.current;
      if (!cId || !uId) return;
      try {
        await renewLock({ variables: { companyId: cId, userId: uId } });
      } catch {
        // Heartbeat failure is non-fatal; lock will expire after 30min
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [companyId, userId, acquireLock, renewLock]);

  // Release lock via mutation
  const releaseLock = useCallback(async () => {
    if (!companyId || !userId) return;
    try {
      await releaseLockMutation({ variables: { companyId, userId } });
    } catch {
      // Best effort
    }
    setLockAcquired(false);
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, [companyId, userId, releaseLockMutation]);

  // beforeunload: use sendBeacon for reliable lock release on tab close
  useEffect(() => {
    if (!companyId || !userId || !lockAcquired) return;

    const endpoint = graphqlEndpoint || '/graphql';

    const handleBeforeUnload = () => {
      const body = JSON.stringify({
        query: `mutation { releaseValidationLock(companyId: ${companyId}, userId: ${userId}) { success } }`,
      });
      navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [companyId, userId, lockAcquired, graphqlEndpoint]);

  // Release lock on unmount
  useEffect(() => {
    return () => {
      const { companyId: cId, userId: uId } = lockParamsRef.current;
      if (cId && uId) {
        releaseLockMutation({ variables: { companyId: cId, userId: uId } }).catch(() => {});
      }
    };
  }, [releaseLockMutation]);

  return { lockAcquired, lockError, releaseLock };
};
